import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Placeholder, VisitorIdentification, isExperienceEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';
import GlobalStyles from './theme/global-styles';
import Footer from './components/ReactComponent/Footer';
import './theme/bootstrap.css';
import ChatWindow from './components/ReactComponent/ChatWindow';
import initGoogleAnalytics from './utils/googleAnalytics';
import SystemError from './components/ReactComponent/Error/SystemError';
import LoadingIndicator from './components/ReactComponent/LoadingIndicator';
import AccountSideMenu from './components/ReactComponent/AccountSideMenu';
import CookiePopup from './components/ReactComponent/CookiePopup';
import AutoLogout from './components/ReactComponent/AutoLogout';
import UpdatesAlert from './components/UpdatesAlert';
import { MediaQuery } from './theme/style-helpers';
import { fluidRoutes } from './utils/constants';
// Using bootstrap is completely optional. It's used here to provide a clean layout for samples,
// without needing extra CSS in the sample app. Remove it in package.json as well if it's removed here.

/*
  APP LAYOUT
  This is where the app's HTML structure and root placeholders should be defined.

  All routes share this root layout by default (this could be customized in RouteHandler),
  but components added to inner placeholders are route-specific.
*/

const FluidContainer = styled.div`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
`;

const Container = styled.div`
padding: 0 20px;

  ${MediaQuery.small`
    padding: 0 60px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    >div, >main {
      max-width: 1320px;
      width: 100%;
    }
  `};
`;

function Layout({ route, isFluid, isssr }) {
  const routeName = route.displayName;
  const fluid = (isFluid || fluidRoutes.indexOf(routeName) > -1);

  initGoogleAnalytics(!isssr && t('googleAnalyticsKey'));

  return (
    <>
      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <Helmet>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Montserrat:300,400,500,600" />
        <title>
          {(route.fields && route.fields.pageTitle && route.fields.pageTitle.value) || 'Page'}
        </title>
      </Helmet>
      <GlobalStyles />

      {/*
      VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
      If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
      For XM (CMS-only) apps, this should be removed.

      VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
    */}
      <VisitorIdentification />
      <AutoLogout />
      {!isExperienceEditorActive() && <SystemError />}
      <LoadingIndicator />
      <AccountSideMenu />
      {!isssr && <CookiePopup />}
      {/* root placeholder for the app, which we add components to using route data */}
      {/* Global Updates Alert - uses i18n dictionary */}
      <UpdatesAlert
        fields={{
          content: { value: t('UpdatesAlertContent') },
        }}
      />
      <Placeholder name="jss-main-header" rendering={route} />

      {(fluid)
        ? (
          <FluidContainer data-container-type="fluid">
            <ChatWindow />
            <Placeholder name="jss-main" rendering={route} />
          </FluidContainer>
        )
        : null}

      {(!fluid)
        ? (
          <Container
            data-container-type="normal"
            style={{
              alignItems: route.itemId === 'reset-password-page' || route.name === 'reset-password' ? 'unset' : 'center',
            }}
          >
            <ChatWindow />
            <Placeholder name="jss-main" rendering={route} />
          </Container>
        )
        : null}

      <Placeholder name="jss-main-footer" rendering={route} />

      <Footer />
    </>
  );
}

Layout.propTypes = {
  route: PropTypes.shape({
    displayName: PropTypes.string,
    itemId: PropTypes.string,
    name: PropTypes.string,
    fields: PropTypes.shape({
      pageTitle: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
  }),
  isFluid: PropTypes.bool,
  isssr: PropTypes.bool,
};

export default Layout;
